/**
 * Atelier statement — horizontal drift fallback.
 *
 * Publishes `--atelier-statement-progress`, running from -1 as the section
 * enters the viewport to +1 as it leaves. The section stylesheet multiplies it
 * by the merchant's drift distance and inverts the sign on the second line, so
 * the two lines slide past each other in opposite directions.
 *
 * Nothing runs when the browser supports `animation-timeline: view()` (the CSS
 * handles it) or when the visitor prefers reduced motion. Every listener,
 * observer and animation frame is released in `disconnectedCallback`, which is
 * what keeps the section clean through theme editor re-renders.
 */

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';
const PROGRESS_PROPERTY = '--atelier-statement-progress';
const OBSERVER_OPTIONS = { rootMargin: '25% 0px 25% 0px' };

class AtelierStatement extends HTMLElement {
  /** @type {IntersectionObserver | null} */
  #observer = null;

  /** @type {number} */
  #frame = 0;

  /** @type {boolean} */
  #listening = false;

  connectedCallback() {
    if (this.#observer || !this.#shouldAnimate()) return;

    this.#observer = new IntersectionObserver(this.#handleIntersection, OBSERVER_OPTIONS);
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#observer = null;
    this.#stopListening();
    this.#cancelFrame();
    this.style.removeProperty(PROGRESS_PROPERTY);
  }

  /**
   * @returns {boolean} True when this element has to drive the drift itself.
   */
  #shouldAnimate() {
    if (typeof window.matchMedia === 'function' && window.matchMedia(REDUCED_MOTION_QUERY).matches) {
      return false;
    }

    if (typeof CSS !== 'undefined' && typeof CSS.supports === 'function') {
      if (CSS.supports('animation-timeline', 'view()')) return false;
    }

    return true;
  }

  /**
   * @param {IntersectionObserverEntry[]} entries
   */
  #handleIntersection = (entries) => {
    const visible = entries.some((entry) => entry.isIntersecting);

    if (visible === this.#listening) return;

    if (visible) {
      this.#startListening();
      this.#requestFrame();
    } else {
      this.#stopListening();
      this.#cancelFrame();
    }
  };

  #startListening() {
    if (this.#listening) return;

    window.addEventListener('scroll', this.#requestFrame, { passive: true });
    window.addEventListener('resize', this.#requestFrame, { passive: true });
    this.#listening = true;
  }

  #stopListening() {
    if (!this.#listening) return;

    window.removeEventListener('scroll', this.#requestFrame);
    window.removeEventListener('resize', this.#requestFrame);
    this.#listening = false;
  }

  #requestFrame = () => {
    if (this.#frame) return;

    this.#frame = window.requestAnimationFrame(this.#update);
  };

  #cancelFrame() {
    if (!this.#frame) return;

    window.cancelAnimationFrame(this.#frame);
    this.#frame = 0;
  }

  #update = () => {
    this.#frame = 0;

    const viewport = window.innerHeight || document.documentElement.clientHeight;
    const rect = this.getBoundingClientRect();
    const travel = viewport + rect.height;

    if (travel <= 0) return;

    const entered = (viewport - rect.top) / travel;
    const clamped = Math.min(Math.max(entered, 0), 1);

    this.style.setProperty(PROGRESS_PROPERTY, (clamped * 2 - 1).toFixed(4));
  };
}

if (!customElements.get('atelier-statement')) {
  customElements.define('atelier-statement', AtelierStatement);
}
